/**
 * Wizard step 2 — Prizes (§16.1): pick catalogue prizes/products into `prizes.prizes[]`, set tier, quantity,
 * value, accent colour, fulfillment owner (D09), Mode A product or Mode B brand list + value cap (D10), with a
 * live storefront-style preview. Edits the form only; autosave persists (console.md wizard contract).
 */
import { useQuery } from '@tanstack/react-query';
import { PrizeTier, SelectionMode, type PrizeConfig } from '@windrop/contracts';
import { Button, Field, Input, PrizeCard, Select } from '@windrop/ui';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { FormErrorSummary, RhfSelect, RhfText } from '../../../components/FormField';
import { useWizard } from '../../lotteries/wizard/WizardFrame';
import { stepForPath, type DraftConfigInput } from '../../lotteries/wizard/steps';
import { catalogApi } from '../api';

const FALLBACK_IMG = `data:image/svg+xml;utf8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect width="400" height="300" fill="#E5E7EB"/></svg>')}`;

export function PrizesStep() {
  const { control, setValue } = useFormContext<DraftConfigInput>();
  const { fields, append, remove } = useFieldArray({ control, name: 'prizes.prizes' });
  const prizes = useWatch({ control, name: 'prizes.prizes' }) as PrizeConfig[] | undefined;
  const currency = (useWatch({ control, name: 'identity.currency' }) as string | undefined) ?? 'XTS';
  const { issues, goToStep } = useWizard();
  const mine = issues.filter((i) => i.step === 'prizes');
  const catalogue = useQuery({ queryKey: ['prizes', {}], queryFn: () => catalogApi.prizes.list() });
  const brands = useQuery({
    queryKey: ['brands', { approved: true }],
    queryFn: () => catalogApi.brands.list({ approved: true }),
  });
  const products = useQuery({ queryKey: ['products'], queryFn: () => catalogApi.products.list() });

  const addFromCatalogue = (prizeId: string) => {
    const p = catalogue.data?.find((x) => x.prizeId === prizeId);
    if (!p) return;
    append({
      prizeId: p.prizeId,
      name: p.name,
      tier: p.tier,
      quantity: p.quantity,
      value: p.value,
      fulfillmentType: p.fulfillmentType,
      fulfillmentOwner: p.fulfillmentOwner,
      ...(p.sponsorId ? { sponsorId: p.sponsorId } : {}),
      selectionMode: p.selectionMode,
      ...(p.productId ? { productId: p.productId } : {}),
      approvedBrandIds: p.approvedBrandIds,
      ...(p.valueCap ? { valueCapMinor: p.valueCap.amountMinor } : {}),
      ...(p.accentColor ? { accentColor: p.accentColor } : {}),
      eligibleRegions: [],
      substitutionPolicy: 'EQUIVALENT_WITH_CONSENT',
    } as never);
  };

  return (
    <div className="wd-stack" data-testid="prizes-step">
      <FormErrorSummary
        issues={mine}
        title={`${mine.length} issue${mine.length === 1 ? '' : 's'} in prizes`}
        onNavigate={(p) => goToStep(stepForPath(p))}
      />
      <div className="wdc-actions-row">
        <Field label="Add from the catalogue" hint="Products, stock and images come from Sponsors & prizes.">
          <Select
            data-testid="catalogue-pick"
            value=""
            onChange={(e) => e.target.value && addFromCatalogue(e.target.value)}
            options={(catalogue.data ?? []).map((p) => ({
              value: p.prizeId,
              label: `${p.name.en ?? p.prizeId} · ${p.tier} · ${p.stock.available} available`,
            }))}
            placeholder={catalogue.isPending ? 'Loading…' : 'Choose a prize'}
          />
        </Field>
        <Button variant="secondary" href="/catalog/products/new">
          Build a new product
        </Button>
      </div>
      {fields.map((f, i) => {
        const p = prizes?.[i];
        const mode = p?.selectionMode ?? 'FIXED_PRODUCT';
        return (
          <fieldset key={f.id} className="wdc-fieldset wd-stack" data-testid={`prize-${i}`}>
            <legend>
              <strong>Prize {i + 1}</strong> — {p?.name?.en ?? 'untitled'}
            </legend>
            <div className="wdc-form-grid">
              <RhfText control={control} name={`prizes.prizes.${i}.name.en`} label="Name" required />
              <RhfSelect
                control={control}
                name={`prizes.prizes.${i}.tier`}
                label="Tier"
                required
                options={PrizeTier.options.map((t) => ({ value: t, label: t }))}
              />
              <RhfText
                control={control}
                name={`prizes.prizes.${i}.quantity`}
                label="Quantity"
                required
                type="number"
                parse={(r) => Number(r)}
              />
              <RhfText
                control={control}
                name={`prizes.prizes.${i}.value.amountMinor`}
                label={`Retail value (minor units, ${currency})`}
                required
                parse={(r) => r.replace(/[^0-9]/g, '')}
              />
              <RhfSelect
                control={control}
                name={`prizes.prizes.${i}.selectionMode`}
                label="Selection mode (D10)"
                options={SelectionMode.options.map((m) => ({
                  value: m,
                  label:
                    m === 'FIXED_PRODUCT'
                      ? 'A · fixed product with options'
                      : 'B · choose within a value cap',
                }))}
              />
              {mode === 'FIXED_PRODUCT' ? (
                <RhfSelect
                  control={control}
                  name={`prizes.prizes.${i}.productId`}
                  label="Product"
                  hint="Option axes, images and SKU stock come from the product."
                  options={(products.data ?? []).map((x) => ({
                    value: x.productId,
                    label: `${x.name.en} (${x.variantCount} SKUs)`,
                  }))}
                />
              ) : (
                <>
                  <RhfText
                    control={control}
                    name={`prizes.prizes.${i}.valueCapMinor`}
                    label={`Value cap (minor units, ${currency})`}
                    required
                    parse={(r) => r.replace(/[^0-9]/g, '')}
                  />
                  <Field
                    label="Approved brands"
                    hint="Winners choose from these brands only; enforced server-side (§6.2)."
                  >
                    <div className="wdc-check-list">
                      {(brands.data ?? []).map((b) => (
                        <label key={b.brandId}>
                          <input
                            type="checkbox"
                            checked={(p?.approvedBrandIds ?? []).includes(b.brandId)}
                            onChange={(e) => {
                              const cur = p?.approvedBrandIds ?? [];
                              setValue(
                                `prizes.prizes.${i}.approvedBrandIds`,
                                e.target.checked ? [...cur, b.brandId] : cur.filter((x) => x !== b.brandId),
                                { shouldDirty: true, shouldValidate: true },
                              );
                            }}
                          />{' '}
                          {b.name}
                        </label>
                      ))}
                    </div>
                  </Field>
                </>
              )}
              <RhfText
                control={control}
                name={`prizes.prizes.${i}.accentColor`}
                label="Accent colour"
                hint="Themes this prize's cards, win screen and claim page; contrast-checked at publish."
              />
              <RhfText
                control={control}
                name={`prizes.prizes.${i}.fulfillmentOwner.name`}
                label="Fulfillment owner (D09)"
                required
                hint="One accountable owner per prize; a missing owner blocks publication."
              />
              <RhfText
                control={control}
                name={`prizes.prizes.${i}.fulfillmentOwner.ownerId`}
                label="Owner id"
                required
              />
              <RhfText
                control={control}
                name={`prizes.prizes.${i}.fulfillmentOwner.backupOwnerId`}
                label="Backup owner"
              />
              <RhfText
                control={control}
                name={`prizes.prizes.${i}.fulfillmentOwner.dispatchSlaBusinessDays`}
                label="Dispatch SLA (business days)"
                type="number"
                parse={(r) => (r === '' ? undefined : Number(r))}
              />
            </div>
            <div className="wdc-prize-row">
              <div style={{ width: 240 }} data-testid={`prize-preview-${i}`}>
                <PrizeCard
                  title={p?.name?.en ?? 'Prize'}
                  imageUrl={
                    catalogue.data?.find((x) => x.prizeId === p?.prizeId) ? FALLBACK_IMG : FALLBACK_IMG
                  }
                  imageAlt={p?.name?.en ?? 'Prize'}
                  accent={p?.accentColor}
                  valueText={
                    p?.value?.amountMinor
                      ? `${currency} ${(Number(p.value.amountMinor) / 100).toLocaleString()} · ${p.quantity ?? 1} unit${(p.quantity ?? 1) === 1 ? '' : 's'}`
                      : undefined
                  }
                  badge={mode === 'VALUE_CAP' ? 'Choose within the cap' : undefined}
                />
              </div>
              <Button variant="quiet" onClick={() => remove(i)}>
                Remove prize
              </Button>
            </div>
          </fieldset>
        );
      })}
      {fields.length === 0 ? <p className="wd-muted">At least one prize is required (§5.4).</p> : null}
      <Field
        label="Prize liability cap (minor units)"
        hint="Optional ceiling on quantity × value plus number-pick exposure (§5.4)."
      >
        <Input
          value={String(
            (useWatch({ control, name: 'prizes.prizeLiabilityCapMinor' }) as string | undefined) ?? '',
          )}
          onChange={(e) =>
            setValue(
              'prizes.prizeLiabilityCapMinor',
              (e.target.value.replace(/[^0-9]/g, '') || undefined) as never,
              { shouldDirty: true },
            )
          }
        />
      </Field>
    </div>
  );
}
